import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { XCloseIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { List, Trigger } from '@radix-ui/react-tabs';
import React, { useLayoutEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import { Button } from '../Button';
import { Stack } from '../Layout';
import { StyledDrawer, StyledDrawerHeader, StyledResizable, StyledDrawerContent, DrawerTabRoot, DrawerTab, StyledCloseButtonWrapper } from './styled';
import { tokens } from './tokens';
export function Drawer({ open, onClose, header, children, maxHeight = '100%', minHeight = '100px', offset = 0, hideHeader = false, ...restProps }) {
    if (!open)
        return null;
    return (_jsx(StyledResizable, { ...restProps, direction: 'top', maxHeight: maxHeight, minHeight: minHeight, bottomOffset: offset, children: _jsxs(StyledDrawer, { children: [!hideHeader && (_jsx(StyledDrawerHeader, { direction: 'horizontal', alignY: 'center', children: _jsxs(Stack, { grow: '1', direction: 'horizontal', alignY: 'center', style: { justifyContent: 'space-between' }, children: [header, _jsx(StyledCloseButtonWrapper, { children: _jsx(Button, { variant: 'plain', onClick: onClose, disablePointerFocus: true, icon: _jsx(XCloseIcon, { type: 'default', color: tokens.Drawer.Icon.color }), "aria-label": 'Close drawer', "data-test-id": 'drawer-close-button' }) })] }) })), _jsx(StyledDrawerContent, { grow: '1', children: children })] }) }));
}
export function DrawerTabHeader({ tabs, activeKey, onChange, className, tabBarExtraContent }) {
    let _activeKey = activeKey || tabs[0]?.key;
    return (_jsx(DrawerTabRoot, { value: _activeKey, onValueChange: onChange, className: className, dir: 'ltr', children: _jsx(List, { asChild: true, children: _jsxs(Stack, { direction: 'horizontal', alignY: 'center', align: 'space-between', children: [tabBarExtraContent?.left && _jsx(Stack, { padding: `0 ${theme.spaces.x16}`, children: tabBarExtraContent?.left }), _jsx(Stack, { direction: 'horizontal', alignY: 'center', children: tabs.map(({ key, tab }) => (_jsx(React.Fragment, { children: _jsx(Trigger, { asChild: true, value: key, children: _jsx(DrawerTab, { variant: 'tertiaryGray', size: 's', disablePointerFocus: true, "data-test-id": `drawer-tab-${key}-button`, children: tab }) }) }, key))) }), tabBarExtraContent?.right && _jsx(Stack, { children: tabBarExtraContent?.right })] }) }) }));
}
export const defaultDrawerheight = 260;
export const initialDrawerState = {
    drawerHeight: defaultDrawerheight,
    expand: false
};
// TODO: this is not needed anymore as per @kajari, we should remove this after all the components using this are updated
export const useDrawerHeight = () => {
    const [drawerState, setDrawerState] = useImmer(initialDrawerState);
    const [maxHeight, setMaxHeight] = useState(() => document.documentElement.clientHeight - 20);
    useLayoutEffect(() => {
        const root = document.getElementById('root');
        if (!root)
            return;
        const calculateHeight = () => {
            const rootHeight = root.clientHeight;
            setMaxHeight(rootHeight - 169);
        };
        calculateHeight();
        const observer = new ResizeObserver(calculateHeight);
        observer.observe(root);
        return () => observer.disconnect();
    }, []);
    const onExpand = usePersistentCallback((height) => {
        setDrawerState((draft) => {
            const isMinHeightReached = height !== undefined && height <= defaultDrawerheight && draft.expand;
            const isMaxHeightReached = height !== undefined && ((height >= maxHeight && !draft.expand) || (height < maxHeight && draft.expand));
            const isExpandToggle = height === undefined;
            if (isExpandToggle || isMinHeightReached || isMaxHeightReached) {
                draft.expand = !draft.expand;
            }
            draft.drawerHeight = isExpandToggle
                ? draft.expand
                    ? maxHeight
                    : defaultDrawerheight
                : (height ?? defaultDrawerheight);
        });
    });
    return {
        drawerHeight: drawerState.drawerHeight,
        maxHeight,
        onExpand
    };
};
